import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FundLookupDtoIn } from '@/models/in/NedarimDecodeModels';
import Button from '@/components/ui/Button';
import styles from './fundAllocationTabs.module.scss';
import { filterAndRankFundPickerMatches } from './fundPickerMatches';

interface FundPickerProps {
  funds: FundLookupDtoIn[];
  /** Called when the office confirms a real fund. */
  onConfirm: (fund: FundLookupDtoIn) => void;
  disabled?: boolean;
  confirmLabel?: string;
  /** A fund to pre-fill (used when correcting an existing assignment). */
  initialFund?: FundLookupDtoIn | null;
}

/**
 * A searchable fund selector. Only a fund that exists in the catalog can be confirmed, so a
 * transaction can never be assigned to a non-existent fund from the UI. Matching is by fund number
 * or name; the chosen value must still reflect a real pick when "assign" is pressed.
 */
const FundPicker = ({ funds, onConfirm, disabled, confirmLabel, initialFund = null }: FundPickerProps) => {
  const { t } = useTranslation();
  const display = (f: FundLookupDtoIn) => `${f.id} — ${f.name}`;

  const [text, setText] = useState(initialFund ? display(initialFund) : '');
  const [chosen, setChosen] = useState<FundLookupDtoIn | null>(initialFund);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);

  const query = text.trim();
  const matches = useMemo(() => filterAndRankFundPickerMatches(funds, query), [query, funds]);

  const validChosen = chosen && text === display(chosen) ? chosen : null;
  const noMatch = query.length > 0 && !validChosen && matches.length === 0;

  const pick = (fund: FundLookupDtoIn) => {
    setChosen(fund);
    setText(display(fund));
    setOpen(false);
  };

  return (
    <div className={styles.picker}>
      <div className={styles.pickerRow}>
        <input
          className={`${styles.pickerInput} ${validChosen ? styles.pickerInputChosen : ''} ${noMatch ? styles.pickerInputInvalid : ''}`}
          placeholder={t('nedarimTransactionsFundAllocation.tabs.searchFundPlaceholder')}
          value={text}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-label={t('nedarimTransactionsFundAllocation.tabs.searchFund')}
          onChange={(e) => {
            setText(e.target.value);
            setChosen(null);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => setOpen(false), 150);
          }}
        />
        <Button
          variant="primary"
          disabled={disabled || !validChosen}
          onMouseDown={() => window.clearTimeout(blurTimer.current)}
          onClick={() => validChosen && onConfirm(validChosen)}
        >
          {confirmLabel ?? t('nedarimTransactionsFundAllocation.tabs.assign')}
        </Button>
      </div>

      {open && matches.length > 0 && (
        <ul className={styles.dropdown}>
          {matches.map((fund) => (
            <li key={fund.id}>
              <button
                type="button"
                className={styles.option}
                onMouseDown={() => {
                  window.clearTimeout(blurTimer.current);
                  pick(fund);
                }}
              >
                <span className={styles.optionId}>{fund.id}</span>
                <span>{fund.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {noMatch && <span className={styles.pickerError}>{t('nedarimTransactionsFundAllocation.tabs.noFundFound')}</span>}
    </div>
  );
};

export default FundPicker;
